"use client";

import { useRef, useEffect } from "react";
import * as Plot from "@observablehq/plot";
import { max } from "d3";
import useElementSize from "@/app/hooks/useElementSize";
import styles from "./styles.module.css";

// Define the OutputItem interface
interface OutputItem {
  x: number; // Month of the year (0 = January, 11 = December)
  y: number; // Day of the week (0 = Sunday, 6 = Saturday)
  count: number;
  date: string;
}

// Define the props for the PlotFigure component
interface PlotFigureProps {
  data: OutputItem[];
}

const PlotChart: React.FC<PlotFigureProps> = ({ data }) => {
  const plotRef = useRef<HTMLDivElement>(null);
  const { width } = useElementSize(plotRef);

  useEffect(() => {
    if (plotRef.current) {
      // Clear any existing plot
      plotRef.current.innerHTML = "";

      const colorThresholds = [0, 5, 10, 15, 20];
      const colorRange = [
        "#5a728210",
        "#ccebc5",
        "#a8ddb5",
        "#7bccc4",
        "#43a2ca",
        "#0868ac",
      ];

      // Determine the maximum week index
      const maxWeek = max(data, (d) => d.x) || 0 + 0;
      const cellSize = width / maxWeek || 0;
      // Create the plot
      const plot = Plot.plot({
        width: maxWeek * cellSize,
        height: 320,
        padding: 0,
        color: {
          type: "threshold",
          domain: colorThresholds, // [1, 5, 10, 20]
          range: colorRange,
          legend: true,
          label: "Count of proposals",
          unknown: "#5a728210",
        },
        x: {
          type: "band",
          label: "",
          domain: data.map((d) => d.x),
          ticks:
            width > 798
              ? [2, 6, 10, 14, 18, 22, 26, 30, 34, 38, 42, 46, 50]
              : [2, 14, 26, 38, 50],
          tickFormat: (week: number) => {
            const formatter = new Intl.DateTimeFormat("en", { month: "short" });
            const month = formatter.format(new Date(data[week * 7].date));
            return month;
          },
          axis: "top",
        },
        y: {
          type: "band",
          label: "",
          domain: [0, 1, 2, 3, 4, 5, 6],
          tickFormat: (day: number) =>
            ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][day],
          ticks: [1, 3, 5],
        },
        marks: [
          Plot.cell(data, {
            x: (d) => d.x,
            y: (d) => d.y,
            fill: (d) => d.count || undefined,
            inset: width > 798 ? 2 : 0.5,
            rx: 2,
            title: (d) => `Date: ${d.date}\nProposals: ${d.count}`,
          }),
        ],
      });

      // Append the plot to the div
      plotRef.current.appendChild(plot);
    }
  }, [data, width]);

  return <div className={styles.plotChart} ref={plotRef}></div>;
};

export default PlotChart;
